import React, { useState, useEffect } from "react";
import "../styles/InternalUsersPage.css";
import { FaSearch, FaTrash } from "react-icons/fa";
import { usePagination } from '../hooks/usePagination';
import Pagination from '../components/Pagination';
import avatar1 from "../assets/avatar1.png";
import { API_BASE_URL, getTenantId } from "../config/api";

// === Modal ajout employé ===
function ModalAddEmployee({ onClose, onAdd }) {
  const currentTenantId = getTenantId();
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    password: "",
    role: "Administration",
    tenant_id: currentTenantId,
    user_type: "admin",
    avatar: avatar1,
  });

  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);

    try {
      const res = await fetch(`${API_BASE_URL}/auth/register`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          email: formData.email,
          phone: formData.phone,
          password: formData.password,
          tenant_id: formData.tenant_id,
          user_type: formData.user_type,
        }),
      });

      const data = await res.json();

      if (!res.ok) {
        setError(data.message || "Erreur lors de l'inscription.");
        setLoading(false);
        return;
      }

      // Envoi du nouvel utilisateur à la page principale
      onAdd({
        name: formData.name || formData.email,
        phone: formData.phone,
        email: formData.email,
        role: formData.role,
        avatar: avatar1,
      });

      onClose();
    } catch (err) {
      setError("Erreur réseau. Veuillez réessayer.");
    }

    setLoading(false);
  };

  return (
    <div className="modal-overlay">
      <div className="modal-content">
        <h3>Ajouter un employé</h3>
        <form onSubmit={handleSubmit} className="add-employee-form">
          <label>Nom et prénom :</label>
          <input name="name" value={formData.name} onChange={handleChange} required />

          <label>Téléphone :</label>
          <input name="phone" value={formData.phone} onChange={handleChange} required />

          <label>Email :</label>
          <input name="email" type="email" value={formData.email} onChange={handleChange} required />

          <label>Mot de passe :</label>
          <input name="password" type="password" value={formData.password} onChange={handleChange} required />

          <label>Rôle :</label>
          <select name="role" value={formData.role} onChange={handleChange}>
            <option>Administration</option>
            <option>Trésorier</option>
          </select>

          {error && <p style={{ color: "red", fontSize: "13px" }}>{error}</p>}

          <div className="modal-actions">
            <button type="submit" className="submit-button" disabled={loading}>
              {loading ? "Ajout en cours..." : "Ajouter"}
            </button>
            <button type="button" className="close-button" onClick={onClose}>
              Annuler
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// === Modal confirmation suppression ===
function ModalConfirmDelete({ user, onConfirm, onCancel }) {
  return (
    <div className="modal-overlay">
      <div className="modal-content">
        <h3>Confirmation de suppression</h3>
        <p>Voulez-vous vraiment supprimer <strong>{user.name}</strong> ?</p>
        <div className="modal-actions">
          <button className="close-button" onClick={onCancel}>Annuler</button>
          <button className="submit-button" onClick={() => onConfirm(user.id)}>Confirmer</button>
        </div>
      </div>
    </div>
  );
}

// === Page principale ===
export default function InternalUsersPage() {
  const [showModal, setShowModal] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);
  const [users, setUsers] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const tenantId = "bfc2a969-f920-4a51-b8b9-76fe5be6da1b";

  // Filter users based on search
  const filteredUsers = users.filter(user =>
    user.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    user.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
    user.role.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Pagination
  const pagination = usePagination(filteredUsers, 'users');

  // Reset pagination when search changes
  useEffect(() => {
    pagination.resetPagination();
  }, [searchTerm]);

  const fetchUsers = async () => {
    try {
      const res = await fetch(`${API_BASE_URL}/auth/users/${tenantId}`);
      const data = await res.json();

      if (!res.ok) {
        console.error(data.message || "Erreur lors du chargement des utilisateurs");
        return;
      }

      const transformedUsers = data.map((user, index) => ({
        id: index + 1,
        name: `${user.first_name || ""} ${user.last_name || ""}`.trim() || user.email,
        email: user.email,
        phone: user.phone,
        role: user.user_type === "admin" ? "Administration" : user.user_type,
        avatar: avatar1,
      }));

      setUsers(transformedUsers);
    } catch (err) {
      console.error("Erreur réseau :", err);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, []);

  const handleAddUser = (newUser) => {
    setUsers((prev) => [...prev, { id: prev.length + 1, ...newUser }]);
  };

  const handleConfirmDelete = async (userId) => {
    try {
      const res = await fetch(`${API_BASE_URL}/users/${userId}`, {
        method: "DELETE",
      });

      const data = await res.json();

      if (!res.ok) {
        alert(data.message || "Erreur lors de la suppression.");
        return;
      }

      setUsers(users.filter(user => user.id !== userId));
      setUserToDelete(null);
    } catch (err) {
      console.error("Erreur suppression :", err);
      alert("Erreur réseau.");
    }
  };

  return (
    <div className="internal-users-page">
      <h2 className="page-title">Utilisateur interne</h2>

      <div className="top-bar">
        <div className="search-box">
          <FaSearch className="search-icon" />
          <input 
            type="text" 
            placeholder="Recherche nom, email, tel …" 
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
        <button className="add-button" onClick={() => setShowModal(true)}>
          Ajouter un employé
        </button>
      </div>

      <table className="user-table">
        <thead>
          <tr>
            <th><input type="checkbox" /></th>
            <th>Nom et prénom de l’employé <span className="sort-icon">⇅</span></th>
            <th>Téléphone <span className="sort-icon">⇅</span></th>
            <th>Email <span className="sort-icon">⇅</span></th>
            <th>Rôle <span className="sort-icon">⇅</span></th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          {pagination.paginatedData.map(user => (
            <tr key={user.id}>
              <td><input type="checkbox" /></td>
              <td className="name-cell">
                <img src={user.avatar} alt="avatar" className="avatar" />
                {user.name}
              </td>
              <td>{user.phone}</td>
              <td>{user.email}</td>
              <td>{user.role}</td>
              <td>
                <FaTrash className="delete-icon" onClick={() => setUserToDelete(user)} />
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {/* Pagination */}
      <Pagination
        totalItems={pagination.totalItems}
        totalPages={pagination.totalPages}
        currentPage={pagination.currentPage}
        pageSize={pagination.pageSize}
        startIndex={pagination.startIndex}
        endIndex={pagination.endIndex}
        hasNextPage={pagination.hasNextPage}
        hasPreviousPage={pagination.hasPreviousPage}
        isFirstPage={pagination.isFirstPage}
        isLastPage={pagination.isLastPage}
        onPageChange={pagination.goToPage}
        onPageSizeChange={pagination.changePageSize}
        config={pagination.config}
      />

      {showModal && (
        <ModalAddEmployee
          onClose={() => setShowModal(false)}
          onAdd={handleAddUser}
        />
      )}

      {userToDelete && (
        <ModalConfirmDelete
          user={userToDelete}
          onConfirm={handleConfirmDelete}
          onCancel={() => setUserToDelete(null)}
        />
      )}
    </div>
  );
}
